import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { InputErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Address } from "@/services/backend/addresses/types/address";

interface BuildingAddressCardProps {
  buildingAddress: Address;
  onBuildingAddressChange: (buildingAddress: Address) => void;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
}

/**
 * BuildingAddressCard is a shared component limiting the ability
 * to mutate the Address to:
 * - company
 * - street
 * - postalCode
 * - city
 * - country
 */
export function BuildingAddressCard({
  buildingAddress,
  onBuildingAddressChange,
  fieldErrors = undefined,
  resetFieldError = undefined,
  title = t("Kundenadresse"),
  description = t("Adresse des Kunden"),
  disabled = false,
}: BuildingAddressCardProps) {
  const errorMessages = (field: string): string[] => {
    if (!field || field === "") {
      return [];
    }

    return fieldErrors ? (fieldErrors[field] ?? []) : [];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="company">{t("Firma")}</Label>
          <Input
            type="text"
            id="company"
            className={
              errorMessages("company").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Firma")}
            value={buildingAddress.company}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("company");
              }
              onBuildingAddressChange({
                ...buildingAddress,
                company: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("company")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="street">{t("Straße")}</Label>
          <Input
            type="text"
            id="street"
            className={
              errorMessages("street").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Straße")}
            value={buildingAddress.street}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("street");
              }
              onBuildingAddressChange({
                ...buildingAddress,
                street: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("street")} />
        </div>
        <div className="flex space-x-4">
          <div className="grid max-w-xs items-center gap-1.5">
            <Label htmlFor="postalCode">{t("PLZ")}</Label>
            <Input
              type="text"
              id="postalCode"
              className={
                errorMessages("postalCode").length > 0
                  ? "border-red-500 shadow-md"
                  : ""
              }
              placeholder={t("PLZ")}
              value={buildingAddress.postalCode}
              onChange={(e) => {
                if (resetFieldError) {
                  resetFieldError("postalCode");
                }
                onBuildingAddressChange({
                  ...buildingAddress,
                  postalCode: e.target.value,
                });
              }}
              disabled={disabled}
            />
            <InputErrorMessages errors={errorMessages("postalCode")} />
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="city">{t("Stadt")}</Label>
            <Input
              type="text"
              id="city"
              className={
                errorMessages("city").length > 0
                  ? "border-red-500 shadow-md"
                  : ""
              }
              placeholder={t("Stadt")}
              value={buildingAddress.city}
              onChange={(e) => {
                if (resetFieldError) {
                  resetFieldError("city");
                }
                onBuildingAddressChange({
                  ...buildingAddress,
                  city: e.target.value,
                });
              }}
              disabled={disabled}
            />
            <InputErrorMessages errors={errorMessages("city")} />
          </div>
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="country">{t("Land")}</Label>
          <Input
            type="text"
            id="country"
            className={
              errorMessages("country").length > 0
                ? "border-red-500 shadow-md"
                : ""
            }
            placeholder={t("Land")}
            value={buildingAddress.country}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("country");
              }
              onBuildingAddressChange({
                ...buildingAddress,
                country: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("country")} />
        </div>
      </CardContent>
    </Card>
  );
}
